import { environment } from "@/env/env.prod";
import Cookies from "js-cookie";
import type {
  AccessListPage,
  ApplyAccessChangesPayload,
  DocumentTemplate,
  FetchTemplatesRealResult,
  TemplateCursor,
  TemplateRole,
} from "./Documents.types";

// ---------------------------------------------------------------------------
// API client — talks to exports.documents (Functions3/functions,
// helpers/Documents/Documents.controller.js). Every method here is an
// actual network call. Used for every real read/write the Documents module
// performs — template CRUD, access control, and background uploads. The
// self-serve fetch/download flow lives in the Flutter app, not this
// dashboard.
// ---------------------------------------------------------------------------

class DocumentTransactionsService {
  baseApi: string;

  constructor() {
    this.baseApi = `${environment.BASE_URL}/documents`;
  }

  getHeaders() {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${Cookies.get("authToken")}`,
      "X-INSTITUTE": Cookies.get("ins_code"),
      "X-E-ID": Cookies.get("E_id"),
      "X-FACULTY": Cookies.get("Fac") || "1",
      "X-PARENT": Cookies.get("ins_code"),
    };
  }

  /** Multipart requests must NOT set Content-Type — the browser sets it (with the boundary) from the FormData itself. */
  getMultipartHeaders() {
    return {
      Authorization: `Bearer ${Cookies.get("authToken")}`,
      "X-INSTITUTE": Cookies.get("ins_code"),
      "X-E-ID": Cookies.get("E_id"),
      "X-FACULTY": Cookies.get("Fac") || "1",
      "X-PARENT": Cookies.get("ins_code"),
    };
  }

  // -----------------------------------------
  // TEMPLATE CRUD (admin/subadmin/superadmin only — enforced server-side)
  // -----------------------------------------

  async createTemplate(payload: Omit<DocumentTemplate, "id" | "createdBy" | "createdAt" | "updatedAt" | "access">) {
    const res = await fetch(`${this.baseApi}/createTemplate`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify(payload),
    });
    return res.json() as Promise<{ success: boolean; template?: DocumentTemplate; message?: string }>;
  }

  async updateTemplate(templateId: string, updates: Partial<DocumentTemplate>) {
    const res = await fetch(`${this.baseApi}/updateTemplate`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify({ templateId, updates }),
    });
    return res.json() as Promise<{ success: boolean; template?: DocumentTemplate; message?: string }>;
  }

  async deleteTemplate(templateId: string) {
    const res = await fetch(`${this.baseApi}/deleteTemplate`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify({ templateId }),
    });
    return res.json() as Promise<{ success: boolean; message?: string }>;
  }

  // -----------------------------------------
  // BACKGROUND UPLOAD — real multipart upload to Storage
  // -----------------------------------------

  async uploadBackground(file: File): Promise<{ success: boolean; url: string; width: number; height: number; message?: string }> {
    // The backend only stores the file and returns its URL — it doesn't know
    // pixel dimensions (that's a browser-side concept), so we still read
    // those locally the same way the mock did.
    const dimensions = await new Promise<{ width: number; height: number }>((resolve) => {
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(objectUrl); resolve({ width: img.naturalWidth, height: img.naturalHeight }); };
      img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve({ width: 794, height: 1123 }); };
      img.src = objectUrl;
    });

    const formData = new FormData();
    formData.append("file", file);

    const res = await fetch(`${this.baseApi}/uploadBackground`, {
      method: "POST",
      headers: this.getMultipartHeaders(),
      body: formData,
    });
    const result = await res.json();
    return { ...result, ...dimensions };
  }

  // -----------------------------------------
  // ACCESS CONTROL (admin/subadmin/superadmin only)
  // -----------------------------------------

  /**
   * Server-side paginated + exact-match-searchable — never fetches a whole
   * role's granted list in one shot, since it's uncapped in size.
   * Cursor-based (RDB has no OFFSET): pass back the previous page's
   * `nextCursor` verbatim to get the next one.
   */
  async fetchAccessList(
    templateId: string,
    role: TemplateRole,
    opts: { search?: string; limit?: number; cursor?: string | null } = {}
  ): Promise<AccessListPage & { success: boolean; message?: string }> {
    const params = new URLSearchParams({ templateId, role });
    if (opts.search) params.set("search", opts.search);
    if (opts.limit) params.set("limit", String(opts.limit));
    if (opts.cursor) params.set("cursor", opts.cursor);
    const res = await fetch(`${this.baseApi}/fetchAccessList?${params.toString()}`, {
      method: "GET",
      headers: this.getHeaders(),
    });
    return res.json();
  }

  /** Everything staged in one "Apply Changes" review, sent as a single call. */
  async applyAccessChanges(templateId: string, role: TemplateRole, changes: ApplyAccessChangesPayload) {
    const res = await fetch(`${this.baseApi}/applyAccessChanges`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify({ templateId, role, ...changes }),
    });
    return res.json() as Promise<{ success: boolean; template?: DocumentTemplate; message?: string }>;
  }

  // -----------------------------------------
  // FETCH (any authenticated tenant member)
  // -----------------------------------------

  /** Cursor-based — RDB has no OFFSET, so there's no "jump to page N," only "next batch after this cursor." */
  async fetchTemplates(opts: { search?: string; limit?: number; cursor?: TemplateCursor | null } = {}): Promise<FetchTemplatesRealResult> {
    const params = new URLSearchParams();
    if (opts.search) params.set("search", opts.search);
    if (opts.limit) params.set("limit", String(opts.limit));
    if (opts.cursor) {
      params.set("cursorValue", opts.cursor.value);
      params.set("cursorKey", opts.cursor.key);
    }
    const res = await fetch(`${this.baseApi}/fetchTemplates?${params.toString()}`, {
      method: "GET",
      headers: this.getHeaders(),
    });
    return res.json();
  }

  async fetchTemplate(templateId: string) {
    const res = await fetch(`${this.baseApi}/fetchTemplate?templateId=${encodeURIComponent(templateId)}`, {
      method: "GET",
      headers: this.getHeaders(),
    });
    return res.json() as Promise<{ success: boolean; template?: DocumentTemplate; message?: string }>;
  }

  /** Role/identity are derived server-side from the caller's own token — no params needed or accepted. */
  async fetchAvailableTemplates() {
    const res = await fetch(`${this.baseApi}/fetchAvailableTemplates`, {
      method: "GET",
      headers: this.getHeaders(),
    });
    return res.json() as Promise<{ success: boolean; templates: DocumentTemplate[] }>;
  }

  /** Re-verifies access server-side and resolves wildcard values from the caller's own identity. */
  async renderTemplateForUser(templateId: string) {
    const res = await fetch(`${this.baseApi}/renderTemplateForUser?templateId=${encodeURIComponent(templateId)}`, {
      method: "GET",
      headers: this.getHeaders(),
    });
    return res.json() as Promise<{
      success: boolean;
      template?: DocumentTemplate;
      values?: { user_name: string; user_email: string; user_id: string };
      message?: string;
    }>;
  }
}

export default DocumentTransactionsService;
